import { downloadPdfFromBlobResponse } from 'helpers/pdf';

import { BuildingUnitTenancy, BuildingUser, axios } from 'shared';
import { BuildingUnit } from 'store/Tenancy/types';

import {
  AccessMethod,
  AccessPath,
  AccessPathStep,
  Entrance,
  InsuranceRequirementsDocument,
  TenantCoi,
  TenantFloorplan,
  TenantLease
} from './Building/types';
import { SaasLead, TenancyService } from './types';

export const getBuildingUnits = ({ tenancyId }: { tenancyId: number }) => {
  return axios.get<BuildingUnit[]>(`v2/tenancies/${tenancyId}/building_units`);
};

export const getBuildingUnitTenancies = ({ tenancyId }: { tenancyId: number }) => {
  return axios.get<BuildingUnitTenancy[]>(`v2/tenancies/${tenancyId}/building_unit_tenancies`);
};

export const updateBuildingInstruction = ({
  tenancyId,
  instructionId,
  value
}: {
  tenancyId: number;
  instructionId: number;
  value: string;
}) => {
  return axios.put(`v2/tenancies/${tenancyId}/building_instructions/${instructionId}`, { value });
};

export const createBuildingInstruction = ({
  tenancyId,
  name,
  value
}: {
  tenancyId: number;
  name: string;
  value: string;
}) => {
  return axios.post(`v2/tenancies/${tenancyId}/building_instructions`, { name, value });
};

export const deleteBuildingInstruction = ({
  tenancyId,
  instructionId
}: {
  tenancyId: number;
  instructionId: number;
}) => {
  return axios.delete(`v2/tenancies/${tenancyId}/building_instructions/${instructionId}`);
};

export const updateBuildingUnitInstruction = ({
  tenancyId,
  instructionId,
  value
}: {
  tenancyId: number;
  instructionId: number;
  value: string;
}) => {
  return axios.put(`v2/tenancies/${tenancyId}/building_unit_instructions/${instructionId}`, { value });
};

export const createBuildingUnitInstruction = ({
  tenancyId,
  buildingUnitId,
  name,
  value
}: {
  tenancyId: number;
  buildingUnitId: number;
  name: string;
  value: string;
}) => {
  return axios.post(`v2/tenancies/${tenancyId}/building_unit_instructions`, {
    building_unit_id: buildingUnitId,
    name,
    value
  });
};

export const deleteBuildingUnitInstruction = ({
  tenancyId,
  instructionId
}: {
  tenancyId: number;
  instructionId: number;
}) => {
  return axios.delete(`v2/tenancies/${tenancyId}/building_unit_instructions/${instructionId}`);
};

export const getSaasLead = ({ tenancyId }: { tenancyId: number }) => {
  return axios.get<SaasLead | null>(`v2/tenancies/${tenancyId}/saas_leads`);
};

export const createSaasLead = ({ tenancyId }: { tenancyId: number }) => {
  return axios.post<SaasLead>(`v2/tenancies/${tenancyId}/saas_leads`);
};

export const getActiveServices = ({ tenancyId }: { tenancyId: number }) => {
  return axios.get<TenancyService[]>(`v2/tenancies/${tenancyId}/services`);
};

export const deletePaymentMethod = ({ paymentMethodId, tenancyId }: { paymentMethodId: string; tenancyId: number }) => {
  return axios.delete(`v2/tenancies/${tenancyId}/payment_methods/${paymentMethodId}`);
};

export const updateTenancyPaymentMethodIds = ({
  paymentMethodIdServices,
  paymentMethodIdRent,
  tenancyId
}: {
  paymentMethodIdServices?: string;
  paymentMethodIdRent?: string;
  tenancyId: number;
}) => {
  const payload: Record<string, string> = {};

  if (paymentMethodIdServices !== undefined) {
    payload.payment_method_id_services = paymentMethodIdServices;
  }

  if (paymentMethodIdRent !== undefined) {
    payload.payment_method_id_rent = paymentMethodIdRent;
  }

  return axios.put(
    `v2/tenancies/${tenancyId}/payment_methods/${
      paymentMethodIdServices ? paymentMethodIdServices : paymentMethodIdRent
    }`,
    payload
  );
};

export const getTenantLeases = ({ tenancyId }: { tenancyId: number }) => {
  return axios.get<TenantLease[]>(`v2/tenancies/${tenancyId}/leases`);
};

export const downloadTenantLease = ({ tenancyId, leaseId }: { tenancyId: number; leaseId: number }) => {
  return axios({
    url: `/v2/tenancies/${tenancyId}/leases/${leaseId}/download`,
    method: 'GET',
    responseType: 'blob'
  }).then(response => {
    downloadPdfFromBlobResponse(response);
  });
};

export const deleteTenantLease = ({ tenancyId, leaseId }: { tenancyId: number; leaseId: number | string }) => {
  // Handle malformed IDs like "1:1" by extracting just the numeric part
  // Convert to string first to handle any type, then extract numeric part
  const idString = String(leaseId);
  const numericPart = idString.split(':')[0].split('-')[0].split(' ')[0];
  const id = parseInt(numericPart, 10);
  if (isNaN(id)) {
    throw new Error(`Invalid lease ID: ${leaseId}`);
  }
  // Double-check the URL is correct before making the request
  const url = `v2/tenancies/${tenancyId}/leases/${id}`;
  if (url.includes(':') && !url.includes('://')) {
    throw new Error(`Malformed URL detected: ${url}`);
  }
  return axios.delete(url);
};

export const uploadTenantLease = ({
  tenancyId,
  file,
  buildingUnitTenancyId,
  onUploadProgress
}: {
  tenancyId: number;
  file: File;
  buildingUnitTenancyId?: number;
  onUploadProgress?: (progressEvent: { loaded: number; total: number }) => void;
}) => {
  const formData = new FormData();
  formData.append('file', file);
  if (buildingUnitTenancyId !== undefined) {
    formData.append('building_unit_tenancy_id', buildingUnitTenancyId.toString());
  }
  return axios.post<TenantLease>(`v2/tenancies/${tenancyId}/leases`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data'
    },
    onUploadProgress
  });
};

export const getTenantCois = ({ tenancyId }: { tenancyId: number }) => {
  return axios.get<TenantCoi[]>(`v2/tenancies/${tenancyId}/cois`);
};

export const downloadTenantCoi = ({ tenancyId, coiId }: { tenancyId: number; coiId: number }) => {
  return axios({
    url: `/v2/tenancies/${tenancyId}/cois/${coiId}/download`,
    method: 'GET',
    responseType: 'blob'
  }).then(response => {
    downloadPdfFromBlobResponse(response);
  });
};

export const deleteTenantCoi = ({ tenancyId, coiId }: { tenancyId: number; coiId: number | string }) => {
  // Handle malformed IDs like "1:1" by extracting just the numeric part
  // Convert to string first to handle any type, then extract numeric part
  const idString = String(coiId);
  const numericPart = idString.split(':')[0].split('-')[0].split(' ')[0];
  const id = parseInt(numericPart, 10);
  if (isNaN(id)) {
    throw new Error(`Invalid COI ID: ${coiId}`);
  }
  // Double-check the URL is correct before making the request
  const url = `v2/tenancies/${tenancyId}/cois/${id}`;
  if (url.includes(':') && !url.includes('://')) {
    throw new Error(`Malformed URL detected: ${url}`);
  }
  return axios.delete(url);
};

export const uploadTenantCoi = ({
  tenancyId,
  file,
  onUploadProgress
}: {
  tenancyId: number;
  file: File;
  buildingUnitTenancyId?: number;
  onUploadProgress?: (progressEvent: { loaded: number; total: number }) => void;
}) => {
  const formData = new FormData();
  formData.append('file', file);
  return axios.post<TenantCoi>(`v2/tenancies/${tenancyId}/cois`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data'
    },
    onUploadProgress
  });
};

export const getTenantFloorplans = ({ tenancyId }: { tenancyId: number }) => {
  return axios.get<TenantFloorplan[]>(`v2/tenancies/${tenancyId}/floorplans`);
};

export const downloadTenantFloorplan = ({ tenancyId, floorplanId }: { tenancyId: number; floorplanId: number }) => {
  return axios({
    url: `/v2/tenancies/${tenancyId}/floorplans/${floorplanId}/download`,
    method: 'GET',
    responseType: 'blob'
  }).then(response => {
    downloadPdfFromBlobResponse(response);
  });
};

export const deleteTenantFloorplan = ({
  tenancyId,
  floorplanId
}: {
  tenancyId: number;
  floorplanId: number | string;
}) => {
  // Handle malformed IDs like "1:1" by extracting just the numeric part
  // Convert to string first to handle any type, then extract numeric part
  const idString = String(floorplanId);
  const numericPart = idString.split(':')[0].split('-')[0].split(' ')[0];
  const id = parseInt(numericPart, 10);
  if (isNaN(id)) {
    throw new Error(`Invalid floorplan ID: ${floorplanId}`);
  }
  // Double-check the URL is correct before making the request
  const url = `v2/tenancies/${tenancyId}/floorplans/${id}`;
  if (url.includes(':') && !url.includes('://')) {
    throw new Error(`Malformed URL detected: ${url}`);
  }
  return axios.delete(url);
};

export const uploadTenantFloorplan = ({
  tenancyId,
  file,
  buildingUnitTenancyId,
  onUploadProgress
}: {
  tenancyId: number;
  file: File;
  buildingUnitTenancyId?: number;
  onUploadProgress?: (progressEvent: { loaded: number; total: number }) => void;
}) => {
  const formData = new FormData();
  formData.append('file', file);
  if (buildingUnitTenancyId !== undefined) {
    formData.append('building_unit_tenancy_id', buildingUnitTenancyId.toString());
  }
  return axios.post<TenantFloorplan>(`v2/tenancies/${tenancyId}/floorplans`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data'
    },
    onUploadProgress
  });
};

export const getTenantInsuranceRequirementsDocuments = ({ tenancyId }: { tenancyId: number }) => {
  return axios.get<InsuranceRequirementsDocument[]>(`v2/tenancies/${tenancyId}/insurance_requirements_documents`);
};

export const downloadTenantInsuranceRequirementsDocument = ({
  tenancyId,
  documentId
}: {
  tenancyId: number;
  documentId: number;
}) => {
  return axios({
    url: `/v2/tenancies/${tenancyId}/insurance_requirements_documents/${documentId}/download`,
    method: 'GET',
    responseType: 'blob'
  }).then(response => {
    downloadPdfFromBlobResponse(response);
  });
};

export const deleteTenantInsuranceRequirementsDocument = ({
  tenancyId,
  documentId
}: {
  tenancyId: number;
  documentId: number | string;
}) => {
  const idString = String(documentId);
  const numericPart = idString.split(':')[0].split('-')[0].split(' ')[0];
  const id = parseInt(numericPart, 10);
  if (isNaN(id)) {
    throw new Error(`Invalid insurance requirements document ID: ${documentId}`);
  }
  const url = `v2/tenancies/${tenancyId}/insurance_requirements_documents/${id}`;
  if (url.includes(':') && !url.includes('://')) {
    throw new Error(`Malformed URL detected: ${url}`);
  }
  return axios.delete(url);
};

export const uploadTenantInsuranceRequirementsDocument = ({
  tenancyId,
  file,
  onUploadProgress
}: {
  tenancyId: number;
  file: File;
  buildingUnitTenancyId?: number;
  onUploadProgress?: (progressEvent: { loaded: number; total: number }) => void;
}) => {
  const formData = new FormData();
  formData.append('file', file);
  return axios.post<InsuranceRequirementsDocument>(
    `v2/tenancies/${tenancyId}/insurance_requirements_documents`,
    formData,
    {
      headers: {
        'Content-Type': 'multipart/form-data'
      },
      onUploadProgress
    }
  );
};

export const createBuildingContact = ({
  tenancyId,
  buildingId,
  firstName,
  lastName,
  email,
  phone,
  callingCode,
  title,
  copyRoutes
}: {
  tenancyId: number;
  buildingId: number;
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  callingCode?: string;
  title: string;
  copyRoutes?: boolean;
}) => {
  const payload: Record<string, any> = {
    building_id: buildingId,
    first_name: firstName,
    last_name: lastName,
    email,
    title
  };

  // Only include phone and calling_code if phone is provided
  if (phone) {
    payload.phone = phone;
    if (callingCode) {
      payload.calling_code = callingCode;
    }
  }

  if (copyRoutes !== undefined) {
    payload.copy_routes = copyRoutes;
  }

  return axios.post<BuildingUser>(`v2/tenancies/${tenancyId}/building_contacts`, payload);
};

export const getPropertyManagerEmailStatus = ({ tenancyId }: { tenancyId: number }) => {
  return axios.get<{
    intro_email_sent: boolean;
    request_building_details_email_sent: boolean;
  }>(`v2/tenancies/${tenancyId}/building_contacts/property_manager_email_status`);
};

export const sendIntroEmailToPropertyManagers = ({ tenancyId }: { tenancyId: number }) => {
  return axios.post(`v2/tenancies/${tenancyId}/building_contacts/send_intro_email_to_property_managers`);
};

export const sendRequestBuildingDetailsEmailToPropertyManagers = ({ tenancyId }: { tenancyId: number }) => {
  return axios.post(
    `v2/tenancies/${tenancyId}/building_contacts/send_request_building_details_email_to_property_managers`
  );
};

export interface TenantOnboardingChecklistItem {
  id: number;
  key: string;
  skipped_at: string | null;
  created_at: string;
  updated_at: string;
}

export const getTenantOnboardingChecklistItems = ({ tenancyId }: { tenancyId: number }) => {
  return axios.get<TenantOnboardingChecklistItem[]>(`v2/tenancies/${tenancyId}/tenant_onboarding_checklist_items`);
};

export interface AccountOwner {
  name: string;
  email: string;
  avatar_url: string | null;
  calendar_link: string | null;
}

export const getAccountOwner = ({ tenancyId }: { tenancyId: number }) => {
  return axios.get<{ account_owner: AccountOwner | null }>(`v2/tenancies/${tenancyId}/account_owner`);
};

export const createTenantOnboardingChecklistItem = ({ tenancyId, key }: { tenancyId: number; key: string }) => {
  return axios.post<TenantOnboardingChecklistItem>(`v2/tenancies/${tenancyId}/tenant_onboarding_checklist_items`, {
    key
  });
};

// Entrances & access methods
export const getEntrances = ({ tenancyId }: { tenancyId: number }) => {
  return axios.get<Entrance[]>(`v2/tenancies/${tenancyId}/entrances`);
};

export const createEntrance = ({
  tenancyId,
  name,
  entrance_type,
  description,
  building_unit_id
}: {
  tenancyId: number;
  name: string;
  entrance_type?: string | null;
  description?: string | null;
  building_unit_id?: number | null;
}) => {
  return axios.post<Entrance>(`v2/tenancies/${tenancyId}/entrances`, {
    name,
    entrance_type: entrance_type ?? null,
    description: description ?? null,
    building_unit_id: building_unit_id ?? null
  });
};

export const updateEntrance = ({
  tenancyId,
  entranceId,
  name,
  entrance_type,
  description,
  building_unit_id
}: {
  tenancyId: number;
  entranceId: number;
  name?: string;
  entrance_type?: string | null;
  description?: string | null;
  building_unit_id?: number | null;
}) => {
  const payload: Record<string, unknown> = {};
  if (name !== undefined) payload.name = name;
  if (entrance_type !== undefined) payload.entrance_type = entrance_type;
  if (description !== undefined) payload.description = description;
  if (building_unit_id !== undefined) payload.building_unit_id = building_unit_id;
  return axios.put<Entrance>(`v2/tenancies/${tenancyId}/entrances/${entranceId}`, payload);
};

export const uploadEntrancePhoto = ({
  tenancyId,
  entranceId,
  file
}: {
  tenancyId: number;
  entranceId: number;
  file: File;
}) => {
  const formData = new FormData();
  formData.append('images[]', file);
  return axios.put<Entrance>(`v2/tenancies/${tenancyId}/entrances/${entranceId}`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
};

export const removeEntrancePhoto = ({ tenancyId, entranceId }: { tenancyId: number; entranceId: number }) => {
  return axios.put<Entrance>(`v2/tenancies/${tenancyId}/entrances/${entranceId}`, {
    remove_photo: true
  });
};

export const deleteEntrance = ({ tenancyId, entranceId }: { tenancyId: number; entranceId: number }) => {
  return axios.delete(`v2/tenancies/${tenancyId}/entrances/${entranceId}`);
};

export const getAccessMethods = ({ tenancyId }: { tenancyId: number }) => {
  return axios.get<AccessMethod[]>(`v2/tenancies/${tenancyId}/access_methods`);
};

export const createAccessMethod = ({
  tenancyId,
  name,
  access_method_type
}: {
  tenancyId: number;
  name?: string;
  access_method_type: string;
}) => {
  const payload: { access_method_type: string; name?: string } = { access_method_type };
  if (name != null && name.trim() !== '') payload.name = name.trim();
  return axios.post<AccessMethod>(`v2/tenancies/${tenancyId}/access_methods`, payload);
};

export const updateAccessMethod = ({
  tenancyId,
  accessMethodId,
  access_method_type
}: {
  tenancyId: number;
  accessMethodId: number;
  access_method_type: string;
}) => {
  return axios.put<AccessMethod>(`v2/tenancies/${tenancyId}/access_methods/${accessMethodId}`, {
    access_method_type
  });
};

export const deleteAccessMethod = ({ tenancyId, accessMethodId }: { tenancyId: number; accessMethodId: number }) => {
  return axios.delete(`v2/tenancies/${tenancyId}/access_methods/${accessMethodId}`);
};

export const getAccessPaths = ({ tenancyId }: { tenancyId: number }) => {
  return axios.get<AccessPath[]>(`v2/tenancies/${tenancyId}/access_paths`);
};

export const updateAccessPath = ({
  tenancyId,
  accessPathId,
  is_published
}: {
  tenancyId: number;
  accessPathId: number;
  is_published: boolean;
}) => {
  return axios.put<AccessPath>(`v2/tenancies/${tenancyId}/access_paths/${accessPathId}`, {
    is_published
  });
};

export const deleteAccessPath = ({ tenancyId, accessPathId }: { tenancyId: number; accessPathId: number }) => {
  return axios.delete(`v2/tenancies/${tenancyId}/access_paths/${accessPathId}`);
};

export const findOrCreateAccessPath = ({
  tenancyId,
  path_type,
  building_unit_id
}: {
  tenancyId: number;
  path_type: string;
  building_unit_id?: number | null;
}) => {
  return axios.post<AccessPath>(`v2/tenancies/${tenancyId}/access_paths/find_or_create`, {
    path_type,
    building_unit_id: building_unit_id ?? null
  });
};

export const createAccessPathStep = ({
  tenancyId,
  accessPathId,
  entrance_id,
  position,
  access_path_step_access_methods
}: {
  tenancyId: number;
  accessPathId: number;
  entrance_id: number;
  position: number;
  access_path_step_access_methods: Array<{ access_method_id: number; instructions?: string }>;
}) => {
  return axios.post<AccessPathStep>(`v2/tenancies/${tenancyId}/access_paths/${accessPathId}/access_path_steps`, {
    entrance_id,
    position,
    access_path_step_access_methods
  });
};

export const updateAccessPathStep = ({
  tenancyId,
  accessPathId,
  accessPathStepId,
  position,
  access_path_step_access_methods
}: {
  tenancyId: number;
  accessPathId: number;
  accessPathStepId: number;
  position?: number;
  access_path_step_access_methods?: Array<{ access_method_id: number; instructions?: string }>;
}) => {
  const body: Record<string, unknown> = {};
  if (position !== undefined) body.position = position;
  if (access_path_step_access_methods !== undefined) {
    body.access_path_step_access_methods = access_path_step_access_methods;
  }
  return axios.patch<AccessPathStep>(
    `v2/tenancies/${tenancyId}/access_paths/${accessPathId}/access_path_steps/${accessPathStepId}`,
    body
  );
};

export const deleteAccessPathStep = ({
  tenancyId,
  accessPathId,
  accessPathStepId
}: {
  tenancyId: number;
  accessPathId: number;
  accessPathStepId: number;
}) => {
  return axios.delete(`v2/tenancies/${tenancyId}/access_paths/${accessPathId}/access_path_steps/${accessPathStepId}`);
};

export const deleteAccessPathStepAccessMethod = ({
  tenancyId,
  accessPathId,
  accessPathStepId,
  accessPathStepAccessMethodId
}: {
  tenancyId: number;
  accessPathId: number;
  accessPathStepId: number;
  accessPathStepAccessMethodId: number;
}) => {
  return axios.delete(
    `v2/tenancies/${tenancyId}/access_paths/${accessPathId}/access_path_steps/${accessPathStepId}/access_path_step_access_methods/${accessPathStepAccessMethodId}`
  );
};
